import type { FC, MutableRefObject } from 'react';
import { memo, useEffect } from 'react';

import { useRefLatest } from '../../../hooks/useRefLatest';
import type { ScrollVisibilityApiType } from '../threadTypes';
import type { ClickBubbleFlowHandler } from './BubbleFlowItem';

interface SelectOnScrollEndProps {
  apiRef: MutableRefObject<ScrollVisibilityApiType>;
  selectedIdRef: MutableRefObject<string | null>;
  selectItem: ClickBubbleFlowHandler;
  ignoreScrollEndWatchRef: MutableRefObject<boolean>;
}

export const SelectOnScrollEnd: FC<SelectOnScrollEndProps> = memo(function SelectOnScrollEnd(
  props,
) {
  const { apiRef, selectedIdRef, selectItem, ignoreScrollEndWatchRef } = props;
  const selectItemRef = useRefLatest(selectItem);

  useEffect(() => {
    const scrollElt = apiRef.current.scrollContainer.current;

    function handleScrollEnd(_event: Event) {
      // Delay to ensure visibleElements was updated by react-horizontal-scrolling-menu. We don't have an easy way to wait for the visible elements to be updated after the scroll ended.
      // If we really want to spend more code on this, we could use the BubbleFlowItem's as watchers, since they can have a hook, useContext(VisibilityContext), that can be used to watch for visible element changes. The process would be:
      // - Make a child element, only rendered on the first BubbleFlowItem (watch only once), using `const { /* visibleElements } = useContext(VisibilityContext);` to watch for visible elements.
      // - on change, emit an event. Find a way to pass the event to here (don't use global event, since it should be only for the current message).
      // - here, on scroll end, start watching that event, and on the first emit, process (set the selected item) + stop watching.

      if (!ignoreScrollEndWatchRef.current) {
        setTimeout(() => {
          const visibleElements = apiRef.current.visibleElements;
          if (visibleElements?.length) {
            const shouldScrollToId = visibleElements[1] || visibleElements[0];

            if (shouldScrollToId !== selectedIdRef.current) {
              selectItemRef.current(shouldScrollToId, { fromScroll: true });
            }
          }
        }, 100);
      }
    }

    scrollElt?.addEventListener('scrollend', handleScrollEnd);

    return () => {
      scrollElt?.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [apiRef, ignoreScrollEndWatchRef, selectItemRef, selectedIdRef]);

  return null;
});
